/* ==========================================================================
   Pricing display helpers (PORTAL-204)
   Pricing is display-only: providers see prices but never pay in the portal.
   ========================================================================== */

import type { PricingList } from '@/api/types'

/** "-15.00" → "15", "12.50" → "12.5" (absolute value, trailing zeros dropped). */
function formatPercent(value: string): string {
  return String(Math.abs(Number(value)))
}

/**
 * One-line summary of how a list overrides default prices, built from its
 * entry counts: "No overrides yet", "10% off default", "+25% markup on
 * default", "8% to 15% off default", "Fixed prices", "Mixed: fixed + free items".
 */
export function describePricingRule(list: PricingList): string {
  const { entry_count, fixed_count, percent_count, free_count } = list
  if (entry_count === 0) return 'No overrides yet'

  if (percent_count === entry_count && list.percent_min !== null && list.percent_max !== null) {
    const min = Number(list.percent_min)
    const max = Number(list.percent_max)
    if (min === max) {
      if (min < 0) return `${formatPercent(list.percent_min)}% off default`
      if (min > 0) return `+${formatPercent(list.percent_min)}% markup on default`
      return 'Same as default'
    }
    if (max <= 0) {
      return `${formatPercent(list.percent_max)}% to ${formatPercent(list.percent_min)}% off default`
    }
    return 'Percent of default (varies)'
  }
  if (fixed_count === entry_count) return 'Fixed prices'
  if (free_count === entry_count) return 'Free items'

  const parts = [
    fixed_count > 0 && 'fixed',
    percent_count > 0 && 'percent',
    free_count > 0 && 'free items',
  ].filter(Boolean)
  return `Mixed: ${parts.join(' + ')}`
}

/** "3 providers" / "1 provider". */
export function providerCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'provider' : 'providers'}`
}
